import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { BeamClipUploader } from "@/components/live/BeamClipUploader";
import { BeamKindBadge } from "@/components/live/BeamRoleBadge";
import { shareBeam } from "@/lib/shareBeam";

const ClipRow = ({ c, isHost, onDelete }) => (
  <div className="p-3 rounded-xl bg-black/30 border border-white/10 space-y-2" data-testid={`beam-clip-${c.clip_id}`}>
    <div className="flex items-center justify-between gap-2">
      <p className="text-sm font-bold text-white truncate">{c.kind === "replay" ? "🎬" : "✨"} {c.title}</p>
      <span className="text-[10px] uppercase tracking-wider text-pink-200/60">{c.kind === "replay" ? "Replay" : "Highlight"}</span>
    </div>
    {c.media_kind === "audio"
      ? <audio controls preload="none" src={c.media_url} className="w-full" data-testid={`beam-clip-player-${c.clip_id}`} />
      : <video controls playsInline preload="metadata" src={c.media_url} className="w-full rounded-lg max-h-80 bg-black" data-testid={`beam-clip-player-${c.clip_id}`} />}
    {isHost && <button type="button" onClick={() => onDelete(c.clip_id)} className="text-[11px] text-rose-300 underline" data-testid={`beam-clip-delete-${c.clip_id}`}>Remove</button>}
  </div>
);

export const BeamEndedPanel = ({ room, isHost }) => {
  const [clips, setClips] = useState(null);
  const load = useCallback(() => api.get(`/live/rooms/${room.room_id}/clips`).then((r) => setClips(r.data.clips)).catch(() => setClips([])), [room.room_id]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    if (!window.confirm("Remove this clip from the Live Beam?")) return;
    try { await api.delete(`/live/rooms/${room.room_id}/clips/${id}`); toast.success("Clip removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };
  return (
    <div className="max-w-2xl mx-auto px-4 pt-20 pb-16 space-y-4" data-testid="beam-ended-panel">
      <Card className="p-5 bg-gradient-to-br from-rose-500/10 via-pink-500/10 to-purple-500/10 border-pink-500/30 space-y-2">
        <BeamKindBadge kind={room.beam_kind} />
        <h1 className="text-2xl font-black text-white" data-testid="beam-ended-title">{room.title}</h1>
        <p className="text-sm text-pink-100/80">This Live Beam has ended{room.host_name ? ` · hosted by ${room.host_name}` : ""}.</p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="outline" onClick={() => shareBeam(room)} className="border-pink-500/40 text-pink-100" data-testid="beam-ended-share">📤 Share</Button>
          <Link to="/beams"><Button size="sm" className="bg-gradient-to-r from-rose-500 to-pink-500 font-bold" data-testid="beam-ended-browse">See who's live</Button></Link>
          <Link to={`/artist/${room.host_user_id}`}><Button size="sm" variant="ghost" className="text-pink-100" data-testid="beam-ended-host-profile">Host profile</Button></Link>
        </div>
      </Card>
      <section className="space-y-2" data-testid="beam-replays-section">
        <h2 className="text-base font-bold text-white">Replay & highlights</h2>
        {clips === null ? <p className="text-sm text-pink-100/60">Loading…</p>
          : clips.length === 0 ? <p className="text-sm text-pink-100/60" data-testid="beam-replays-empty">{isHost ? "Nothing saved yet. Upload your replay or a short highlight below so fans can catch up." : "No replay yet. Follow the host to hear about their next Live Beam."}</p>
          : clips.map((c) => <ClipRow key={c.clip_id} c={c} isHost={isHost} onDelete={del} />)}
        {isHost && <BeamClipUploader roomId={room.room_id} onSaved={load} />}
      </section>
    </div>
  );
};
